import { useState, useEffect, useRef } from 'react';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import SpaceBetween from '@cloudscape-design/components/space-between';
import FormField from '@cloudscape-design/components/form-field';
import Input from '@cloudscape-design/components/input';
import Select from '@cloudscape-design/components/select';
import Checkbox from '@cloudscape-design/components/checkbox';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import type { AssetCategory, Asset } from '@/types/storageInventory';
import { MISSING_STATUS, isUnknownSite } from '@/types/storageInventory';
import { validateAsset } from '@/utils/storageValidation';
import { uploadPhotoFiles } from '@/lib/photoUpload';
import { PhotoImage } from './PhotoImage';

interface AssetFormModalProps {
  visible: boolean;
  category: AssetCategory;
  asset?: Asset;
  existingSites: string[];
  existingMakes: string[];
  existingModels: string[];
  statuses: string[];
  onSubmit: (asset: Omit<Asset, 'id'> | Asset) => void;
  onDismiss: () => void;
}

export function AssetFormModal({
  visible,
  category,
  asset,
  existingSites,
  existingMakes,
  existingModels,
  statuses,
  onSubmit,
  onDismiss,
}: AssetFormModalProps) {
  const isEditMode = !!asset;
  const [formData, setFormData] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (visible) {
      if (asset) {
        setFormData({ ...asset });
      } else {
        setFormData({ status: 'unknown', to_be_decommissioned: false, quantity: 1 });
      }
      setErrors({});
    }
  }, [visible, asset]);

  const handleSubmit = () => {
    // Auto-fill hostname if blank
    const submitData = { ...formData };
    if (category === 'servers' && (!submitData.hostname || String(submitData.hostname).trim() === '')) {
      submitData.hostname = 'no_hostname';
    }

    const result = validateAsset(category, submitData);
    if (!result.valid) {
      setErrors(result.errors);
      return;
    }
    setErrors({});
    if (isEditMode && asset) {
      onSubmit({ ...submitData, id: asset.id } as Asset);
    } else {
      onSubmit(submitData as Omit<Asset, 'id'>);
    }
  };

  const updateField = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // When the audit "Unknown Site" is selected, default the status to Missing/Unknown
  // automatically. The user can still change the status afterward.
  const handleSiteChange = (value: unknown) => {
    setFormData((prev) => {
      const next: Record<string, unknown> = { ...prev, site: value };
      if (isUnknownSite(value)) {
        next.status = MISSING_STATUS;
      }
      return next;
    });
    setErrors((prev) => {
      if (!prev.site && !prev.status) return prev;
      const next = { ...prev };
      delete next.site;
      if (isUnknownSite(value)) delete next.status;
      return next;
    });
  };

  const siteOptions = existingSites.map((s) => ({ label: s, value: s }));
  const makeOptions = existingMakes.map((m) => ({ label: m, value: m }));
  const modelOptions = existingModels.map((m) => ({ label: m, value: m }));

  // Image upload handling
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const images = (formData.images as string[]) || [];

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setPhotoError(null);
    setUploadingPhoto(true);
    try {
      // Upload each photo and store the returned keys (not the image bytes).
      const keys = await uploadPhotoFiles(category, files);
      if (keys.length > 0) {
        setFormData((prev) => ({
          ...prev,
          images: [...((prev.images as string[]) || []), ...keys],
        }));
      }
    } catch (err) {
      setPhotoError(err instanceof Error ? err.message : 'Photo upload failed');
    } finally {
      setUploadingPhoto(false);
      // Reset input so same file can be re-selected
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: ((prev.images as string[]) || []).filter((_, i) => i !== index),
    }));
  };

  const renderImageSection = () => (
    <SpaceBetween size="s">
      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Photos</div>
      <SpaceBetween direction="horizontal" size="s">
        {images.map((img, idx) => (
          <div key={idx} style={{ position: 'relative', display: 'inline-block' }}>
            <PhotoImage
              photo={img}
              alt={`Asset photo ${idx + 1}`}
              style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 4, border: '1px solid #d1d5db' }}
            />
            <button
              onClick={() => handleRemoveImage(idx)}
              style={{
                position: 'absolute', top: -6, right: -6,
                background: '#d13212', color: 'white', border: 'none',
                borderRadius: '50%', width: 20, height: 20, cursor: 'pointer',
                fontSize: 12, lineHeight: '20px', textAlign: 'center',
              }}
              aria-label="Remove image"
            >
              ×
            </button>
          </div>
        ))}
      </SpaceBetween>
      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          multiple
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
        <Button
          iconName="upload"
          onClick={() => fileInputRef.current?.click()}
          loading={uploadingPhoto}
          disabled={uploadingPhoto}
        >
          {uploadingPhoto ? 'Uploading…' : 'Add photo'}
        </Button>
        {photoError && (
          <Box color="text-status-error" fontSize="body-s" margin={{ top: 'xxs' }}>
            {photoError}
          </Box>
        )}
      </div>
    </SpaceBetween>
  );

  const renderServerFields = () => (
    <SpaceBetween size="m">
      <ColumnLayout columns={2}>
        <FormField label="Site *" errorText={errors.site}>
          <Select selectedOption={formData.site ? { label: String(formData.site), value: String(formData.site) } : null} onChange={({ detail }) => handleSiteChange(detail.selectedOption.value)} options={siteOptions} placeholder="Select site" expandToViewport filteringType="auto" />
        </FormField>
        <FormField label="Hostname">
          <Input value={String(formData.hostname || '')} onChange={({ detail }) => updateField('hostname', detail.value)} placeholder="Leave blank for no_hostname" />
        </FormField>
      </ColumnLayout>
      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Identity</div>
      <ColumnLayout columns={2}>
        <FormField label="Make *" errorText={errors.make}>
          <Select
            selectedOption={formData.make ? { label: String(formData.make), value: String(formData.make) } : null}
            onChange={({ detail }) => updateField('make', detail.selectedOption.value)}
            options={makeOptions}
            placeholder="Select make" expandToViewport
            filteringType="auto"
          />
        </FormField>
        <FormField label="Model *" errorText={errors.model} description="Manage models in Settings tab">
          <Select
            selectedOption={formData.model ? { label: String(formData.model), value: String(formData.model) } : null}
            onChange={({ detail }) => updateField('model', detail.selectedOption.value)}
            options={modelOptions}
            placeholder="Select model" expandToViewport
            filteringType="auto"
          />
        </FormField>
        <FormField label="Service Tag *" errorText={errors.service_tag}>
          <Input
            value={String(formData.service_tag || '')}
            onChange={({ detail }) => updateField('service_tag', detail.value)}
          />
        </FormField>
        <FormField label="Express Service Code" description="Dell 7-digit numeric code">
          <Input
            value={String(formData.express_service_code || '')}
            onChange={({ detail }) => updateField('express_service_code', detail.value)}
            placeholder="1234567"
          />
        </FormField>
        <FormField label="Status *" errorText={errors.status}>
          <Select
            selectedOption={formData.status ? { label: String(formData.status), value: String(formData.status) } : null}
            onChange={({ detail }) => updateField('status', detail.selectedOption.value)}
            options={statuses.map((s) => ({ label: s, value: s }))}
            placeholder="Select status" expandToViewport
          />
        </FormField>
      </ColumnLayout>

      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Dates & Warranty</div>
      <ColumnLayout columns={2}>
        <FormField label="Ship Date" description="YYYY-MM-DD" errorText={errors.ship_date}>
          <Input
            value={String(formData.ship_date || '')}
            onChange={({ detail }) => updateField('ship_date', detail.value)}
            placeholder="2024-01-15"
          />
        </FormField>
        <FormField label="Support End Date" description="YYYY-MM-DD" errorText={errors.support_end_date}>
          <Input
            value={String(formData.support_end_date || '')}
            onChange={({ detail }) => updateField('support_end_date', detail.value)}
            placeholder="2027-01-15"
          />
        </FormField>
      </ColumnLayout>

      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Hardware Specs</div>
      <ColumnLayout columns={2}>
        <FormField label="CPU Model">
          <Input
            value={String(formData.cpu_model || '')}
            onChange={({ detail }) => updateField('cpu_model', detail.value)}
          />
        </FormField>
        <FormField label="CPU Quantity">
          <Input
            type="number"
            value={String(formData.cpu_quantity || '')}
            onChange={({ detail }) => updateField('cpu_quantity', detail.value ? parseInt(detail.value) : '')}
          />
        </FormField>
        <FormField label="RAM Model">
          <Input
            value={String(formData.ram_model || '')}
            onChange={({ detail }) => updateField('ram_model', detail.value)}
          />
        </FormField>
        <FormField label="RAM Quantity">
          <Input
            type="number"
            value={String(formData.ram_quantity || '')}
            onChange={({ detail }) => updateField('ram_quantity', detail.value ? parseInt(detail.value) : '')}
          />
        </FormField>
        <FormField label="Disk Model">
          <Input
            value={String(formData.disk_model || '')}
            onChange={({ detail }) => updateField('disk_model', detail.value)}
          />
        </FormField>
        <FormField label="Disk Quantity">
          <Input
            type="number"
            value={String(formData.disk_quantity || '')}
            onChange={({ detail }) => updateField('disk_quantity', detail.value ? parseInt(detail.value) : '')}
          />
        </FormField>
        <FormField label="Embedded NICs">
          <Input
            value={String(formData.embedded_nics || '')}
            onChange={({ detail }) => updateField('embedded_nics', detail.value)}
          />
        </FormField>
        <FormField label="Integrated NICs">
          <Input
            value={String(formData.integrated_nics || '')}
            onChange={({ detail }) => updateField('integrated_nics', detail.value)}
          />
        </FormField>
        <FormField label="Add-on Cards">
          <Input
            value={String(formData.addon_cards || '')}
            onChange={({ detail }) => updateField('addon_cards', detail.value)}
          />
        </FormField>
      </ColumnLayout>

      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Tracking</div>
      <ColumnLayout columns={2}>
        <FormField label="Last Known Contact">
          <Input
            value={String(formData.last_known_contact || '')}
            onChange={({ detail }) => updateField('last_known_contact', detail.value)}
          />
        </FormField>
        <FormField label="Purchase Order">
          <Input
            value={String(formData.purchase_order || '')}
            onChange={({ detail }) => updateField('purchase_order', detail.value)}
          />
        </FormField>
        <FormField label="Cost ($)">
          <Input
            type="number"
            value={String(formData.cost || '')}
            onChange={({ detail }) => updateField('cost', detail.value ? parseFloat(detail.value) : '')}
          />
        </FormField>
        <FormField label="Location Detail">
          <Input
            value={String(formData.location_detail || '')}
            onChange={({ detail }) => updateField('location_detail', detail.value)}
            placeholder="Room > Shelf > Position"
          />
        </FormField>
      </ColumnLayout>

      <FormField label="Notes">
        <Input
          value={String(formData.notes || '')}
          onChange={({ detail }) => updateField('notes', detail.value)}
        />
      </FormField>

      {renderImageSection()}
    </SpaceBetween>
  );

  const renderElementalFields = () => (
    <SpaceBetween size="m">
      <ColumnLayout columns={2}>
        <FormField label="Site *" errorText={errors.site}>
          <Select selectedOption={formData.site ? { label: String(formData.site), value: String(formData.site) } : null} onChange={({ detail }) => handleSiteChange(detail.selectedOption.value)} options={siteOptions} placeholder="Select site" expandToViewport filteringType="auto" />
        </FormField>
        <FormField label="Series *" errorText={errors.series}>
          <Input value={String(formData.series || '')} onChange={({ detail }) => updateField('series', detail.value)} />
        </FormField>
      </ColumnLayout>
      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Identity</div>
      <ColumnLayout columns={2}>
        <FormField label="Manufacturer *" errorText={errors.manufacturer}>
          <Select
            selectedOption={formData.manufacturer ? { label: String(formData.manufacturer), value: String(formData.manufacturer) } : null}
            onChange={({ detail }) => updateField('manufacturer', detail.selectedOption.value)}
            options={makeOptions}
            placeholder="Select manufacturer" expandToViewport
            filteringType="auto"
          />
        </FormField>
        <FormField label="Model *" errorText={errors.model} description="Manage models in Settings tab">
          <Select
            selectedOption={formData.model ? { label: String(formData.model), value: String(formData.model) } : null}
            onChange={({ detail }) => updateField('model', detail.selectedOption.value)}
            options={modelOptions}
            placeholder="Select model" expandToViewport
            filteringType="auto"
          />
        </FormField>
        <FormField label="Service Tag *" errorText={errors.service_tag}>
          <Input
            value={String(formData.service_tag || '')}
            onChange={({ detail }) => updateField('service_tag', detail.value)}
          />
        </FormField>
        <FormField label="Express Service Code" description="Dell 7-digit numeric code">
          <Input
            value={String(formData.express_service_code || '')}
            onChange={({ detail }) => updateField('express_service_code', detail.value)}
            placeholder="1234567"
          />
        </FormField>
        <FormField label="Boxed Status *" errorText={errors.boxed_status}>
          <Select
            selectedOption={formData.boxed_status ? { label: String(formData.boxed_status), value: String(formData.boxed_status) } : null}
            onChange={({ detail }) => updateField('boxed_status', detail.selectedOption.value)}
            options={[
              { label: 'boxed', value: 'boxed' },
              { label: 'unboxed', value: 'unboxed' },
            ]}
            placeholder="Select"
          />
        </FormField>
        <FormField label="Condition *" errorText={errors.condition}>
          <Select
            selectedOption={formData.condition ? { label: String(formData.condition), value: String(formData.condition) } : null}
            onChange={({ detail }) => updateField('condition', detail.selectedOption.value)}
            options={[
              { label: 'new', value: 'new' },
              { label: 'used', value: 'used' },
              { label: 'refurbished', value: 'refurbished' },
            ]}
            placeholder="Select"
          />
        </FormField>
        <FormField label="Status *" errorText={errors.status}>
          <Select
            selectedOption={formData.status ? { label: String(formData.status), value: String(formData.status) } : null}
            onChange={({ detail }) => updateField('status', detail.selectedOption.value)}
            options={statuses.map((s) => ({ label: s, value: s }))}
            placeholder="Select status" expandToViewport
          />
        </FormField>
        <FormField label="Location">
          <Input
            value={String(formData.location || '')}
            onChange={({ detail }) => updateField('location', detail.value)}
          />
        </FormField>
      </ColumnLayout>

      <Checkbox
        checked={!!formData.to_be_decommissioned}
        onChange={({ detail }) => updateField('to_be_decommissioned', detail.checked)}
      >
        To be decommissioned
      </Checkbox>

      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Tracking</div>
      <ColumnLayout columns={2}>
        <FormField label="Last Known Contact">
          <Input
            value={String(formData.last_known_contact || '')}
            onChange={({ detail }) => updateField('last_known_contact', detail.value)}
          />
        </FormField>
        <FormField label="Purchase Order">
          <Input
            value={String(formData.purchase_order || '')}
            onChange={({ detail }) => updateField('purchase_order', detail.value)}
          />
        </FormField>
        <FormField label="Cost ($)">
          <Input
            type="number"
            value={String(formData.cost || '')}
            onChange={({ detail }) => updateField('cost', detail.value ? parseFloat(detail.value) : '')}
          />
        </FormField>
        <FormField label="Location Detail">
          <Input
            value={String(formData.location_detail || '')}
            onChange={({ detail }) => updateField('location_detail', detail.value)}
            placeholder="Room > Shelf > Position"
          />
        </FormField>
      </ColumnLayout>

      <FormField label="Notes">
        <Input
          value={String(formData.notes || '')}
          onChange={({ detail }) => updateField('notes', detail.value)}
        />
      </FormField>

      {renderImageSection()}
    </SpaceBetween>
  );

  const renderNetworkDeviceFields = () => (
    <SpaceBetween size="m">
      <ColumnLayout columns={2}>
        <FormField label="Site *" errorText={errors.site}>
          <Select selectedOption={formData.site ? { label: String(formData.site), value: String(formData.site) } : null} onChange={({ detail }) => handleSiteChange(detail.selectedOption.value)} options={siteOptions} placeholder="Select site" expandToViewport filteringType="auto" />
        </FormField>
        <FormField label="Hostname">
          <Input value={String(formData.hostname || '')} onChange={({ detail }) => updateField('hostname', detail.value)} />
        </FormField>
      </ColumnLayout>
      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Identity</div>
      <ColumnLayout columns={2}>
        <FormField label="Make *" errorText={errors.make}>
          <Select
            selectedOption={formData.make ? { label: String(formData.make), value: String(formData.make) } : null}
            onChange={({ detail }) => updateField('make', detail.selectedOption.value)}
            options={makeOptions}
            placeholder="Select make" expandToViewport
            filteringType="auto"
          />
        </FormField>
        <FormField label="Model *" errorText={errors.model} description="Manage models in Settings tab">
          <Select
            selectedOption={formData.model ? { label: String(formData.model), value: String(formData.model) } : null}
            onChange={({ detail }) => updateField('model', detail.selectedOption.value)}
            options={modelOptions}
            placeholder="Select model" expandToViewport
            filteringType="auto"
          />
        </FormField>
        <FormField label="Serial Number *" errorText={errors.serial_num}>
          <Input
            value={String(formData.serial_num || '')}
            onChange={({ detail }) => updateField('serial_num', detail.value)}
          />
        </FormField>
        <FormField label="Status *" errorText={errors.status}>
          <Select
            selectedOption={formData.status ? { label: String(formData.status), value: String(formData.status) } : null}
            onChange={({ detail }) => updateField('status', detail.selectedOption.value)}
            options={statuses.map((s) => ({ label: s, value: s }))}
            placeholder="Select status" expandToViewport
          />
        </FormField>
        <FormField label="Quantity *" errorText={errors.quantity}>
          <Input
            type="number"
            value={String(formData.quantity || '')}
            onChange={({ detail }) => updateField('quantity', detail.value ? parseInt(detail.value) : '')}
          />
        </FormField>
        <FormField label="Support End Date" description="YYYY-MM-DD" errorText={errors.support_end_date}>
          <Input
            value={String(formData.support_end_date || '')}
            onChange={({ detail }) => updateField('support_end_date', detail.value)}
            placeholder="2027-01-15"
          />
        </FormField>
      </ColumnLayout>

      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Shipping</div>
      <ColumnLayout columns={2}>
        <FormField label="Customer Order">
          <Input
            value={String(formData.customer_order || '')}
            onChange={({ detail }) => updateField('customer_order', detail.value)}
          />
        </FormField>
        <FormField label="Shipping Box">
          <Input
            value={String(formData.shipping_box || '')}
            onChange={({ detail }) => updateField('shipping_box', detail.value)}
          />
        </FormField>
        <FormField label="Tracking Number">
          <Input
            value={String(formData.tracking_num || '')}
            onChange={({ detail }) => updateField('tracking_num', detail.value)}
          />
        </FormField>
      </ColumnLayout>

      <div style={{ borderTop: '2px solid var(--color-border-divider-default, #e9ebed)', margin: '12px 0 8px 0' }} />
      <div style={{ borderLeft: "3px solid #0073bb", paddingLeft: "10px", fontSize: "14px", fontWeight: 600, color: "var(--color-text-heading-default, #000716)" }}>Tracking</div>
      <ColumnLayout columns={2}>
        <FormField label="Last Known Contact">
          <Input
            value={String(formData.last_known_contact || '')}
            onChange={({ detail }) => updateField('last_known_contact', detail.value)}
          />
        </FormField>
        <FormField label="Purchase Order">
          <Input
            value={String(formData.purchase_order || '')}
            onChange={({ detail }) => updateField('purchase_order', detail.value)}
          />
        </FormField>
        <FormField label="Cost ($)">
          <Input
            type="number"
            value={String(formData.cost || '')}
            onChange={({ detail }) => updateField('cost', detail.value ? parseFloat(detail.value) : '')}
          />
        </FormField>
        <FormField label="Location Detail">
          <Input
            value={String(formData.location_detail || '')}
            onChange={({ detail }) => updateField('location_detail', detail.value)}
            placeholder="Room > Shelf > Position"
          />
        </FormField>
      </ColumnLayout>

      <FormField label="Notes">
        <Input
          value={String(formData.notes || '')}
          onChange={({ detail }) => updateField('notes', detail.value)}
        />
      </FormField>

      {renderImageSection()}
    </SpaceBetween>
  );

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      header={isEditMode ? 'Edit asset' : 'Add asset'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              {isEditMode ? 'Save changes' : 'Add asset'}
            </Button>
          </SpaceBetween>
        </Box>
      }
      size="max"
    >
      <div style={{ maxHeight: '70vh', overflowY: 'auto', paddingRight: '8px' }}>
        <SpaceBetween size="s">
          {category === 'servers' && renderServerFields()}
          {category === 'elementals' && renderElementalFields()}
          {category === 'network_devices' && renderNetworkDeviceFields()}
        </SpaceBetween>
      </div>
    </Modal>
  );
}
